'use client';

import { useEffect } from 'react';
import { useApiClient } from '../contexts/ApiContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useIsAuthed } from './useIsAuthed';
import { screenToActivity } from '../lib/presenceActivity';

const HEARTBEAT_INTERVAL_MS = 25_000;

/**
 * Reports the authed user's presence (online + current activity) to the
 * backend. Sends immediately, on every screen change, on a 25s interval, and
 * when the tab returns to the foreground. Stays silent while the tab is
 * hidden so a backgrounded tab drops to "offline" once the 60s TTL lapses.
 */
export function usePresenceHeartbeat(): void {
  const apiClient = useApiClient();
  const isAuthed = useIsAuthed();
  const navigation = useNavigation();
  const activity = screenToActivity(navigation.getCurrentScreen().screen);

  useEffect(() => {
    if (!isAuthed) return;

    const send = () => {
      if (document.visibilityState !== 'visible') return;
      apiClient.api.realtimeControllerHeartbeat({ activity }).catch(() => {});
    };

    send();
    const interval = setInterval(send, HEARTBEAT_INTERVAL_MS);
    document.addEventListener('visibilitychange', send);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', send);
    };
  }, [isAuthed, apiClient, activity]);
}
